import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Plug, Send, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";

const PROVIDERS = [
  { value: "webhook", label: "Generic Webhook (any URL)" },
  { value: "hubspot", label: "HubSpot" },
  { value: "salesforce", label: "Salesforce" },
  { value: "zoho", label: "Zoho CRM" },
  { value: "pipedrive", label: "Pipedrive" },
  { value: "freshsales", label: "Freshsales" },
  { value: "zapier", label: "Zapier / Make.com" },
  { value: "custom", label: "Custom (other)" },
];

const PRESET_HINTS = {
  webhook: "Any HTTPS endpoint that accepts a JSON POST.",
  hubspot: "Workflow webhook URL from HubSpot. API key uses ‘Bearer’.",
  salesforce: "REST endpoint of your Salesforce flow / Apex web service.",
  zoho: "Webhook URL from Zoho CRM Workflow. Use Token scheme.",
  pipedrive: "Use https://api.pipedrive.com/v1/leads?api_token=… or your webhook.",
  freshsales: "Use the Webhook automation URL from Freshsales.",
  zapier: "Paste the Catch Hook URL from Zapier or Make.com.",
  custom: "Any custom endpoint.",
};

export default function IntegrationsManager() {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    api.get("/integrations/crm").then((r) => setData(r.data));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data: out } = await api.put("/integrations/crm", data);
      setData(out);
      toast.success("Integration saved");
    } catch {
      toast.error("Could not save");
    } finally {
      setSaving(false);
    }
  };

  const test = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const { data: r } = await api.post("/integrations/crm/test");
      setTestResult(r);
      if (r.ok) toast.success("Test sent — your CRM acknowledged it.");
      else toast.error(`Test failed: ${r.status}`);
      // re-fetch to get latest sync status
      const fresh = await api.get("/integrations/crm");
      setData(fresh.data);
    } catch (e) {
      toast.error("Test failed — see console");
      setTestResult({ ok: false, status: "request error" });
    } finally {
      setTesting(false);
    }
  };

  if (!data) return <div className="text-earth-600">Loading…</div>;

  const lastOk = (data.last_sync_status || "").startsWith("success");

  return (
    <div data-testid="integrations-manager">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.22em] text-sage-600">External</div>
        <h1 className="font-serif text-4xl text-earth-900 mt-2 font-light">CRM / ERP Integration</h1>
        <p className="text-earth-600 mt-2 max-w-2xl">
          Forward every new enquiry to your CRM or ERP in real time. We POST a JSON payload to your webhook URL the
          moment a booking arrives — works with HubSpot, Salesforce, Zoho, Pipedrive, Zapier, or any custom endpoint.
        </p>
      </div>

      <div className="bg-white rounded-2xl p-8 border border-earth-100 flex flex-col gap-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#FF6B35]/10 ring-1 ring-[#FF6B35]/30 flex items-center justify-center">
              <Plug className="w-5 h-5 text-[#FF6B35]" strokeWidth={1.5} />
            </div>
            <div>
              <Label>Connection enabled</Label>
              <p className="text-xs text-earth-500 mt-1">When off, bookings stay in the dashboard only.</p>
            </div>
          </div>
          <Switch
            checked={data.enabled === true}
            onCheckedChange={(v) => setData({ ...data, enabled: v })}
            data-testid="crm-enabled"
          />
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label>Provider</Label>
            <select
              className="mt-2 w-full bg-white border border-earth-200 rounded-md px-3 py-2 text-sm"
              value={data.provider}
              onChange={(e) => setData({ ...data, provider: e.target.value })}
              data-testid="crm-provider"
            >
              {PROVIDERS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
            <p className="text-xs text-earth-500 mt-2">{PRESET_HINTS[data.provider] || ""}</p>
          </div>
          <div>
            <Label>Webhook URL</Label>
            <Input
              className="mt-2"
              placeholder="https://hooks.example.com/leads"
              value={data.webhook_url || ""}
              onChange={(e) => setData({ ...data, webhook_url: e.target.value })}
              data-testid="crm-webhook"
            />
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-4">
          <div className="md:col-span-1">
            <Label>Auth header name</Label>
            <Input className="mt-2" value={data.auth_header || "Authorization"} onChange={(e) => setData({ ...data, auth_header: e.target.value })} />
          </div>
          <div className="md:col-span-1">
            <Label>Auth scheme</Label>
            <Input className="mt-2" placeholder="Bearer / Token / (empty)" value={data.auth_scheme ?? "Bearer"} onChange={(e) => setData({ ...data, auth_scheme: e.target.value })} />
          </div>
          <div className="md:col-span-1">
            <Label>API key / token</Label>
            <Input
              className="mt-2"
              type="password"
              placeholder="••••••••••"
              value={data.api_key || ""}
              onChange={(e) => setData({ ...data, api_key: e.target.value })}
              data-testid="crm-apikey"
            />
          </div>
        </div>

        <div className="bg-earth-50 rounded-xl p-4 border border-earth-100">
          <div className="text-xs uppercase tracking-[0.22em] text-earth-500 mb-2">Sample payload</div>
          <pre className="text-[11px] text-earth-700 font-mono overflow-auto whitespace-pre-wrap">
{`POST  ${data.webhook_url || "<your webhook URL>"}
Content-Type: application/json
${data.api_key ? `${data.auth_header}: ${data.auth_scheme} ${data.api_key.replace(/./g, "•")}` : ""}

{
  "event": "booking.created",
  "source": "aiyavi-retreat",
  "data": {
    "id": "<uuid>",
    "name": "Visitor name",
    "phone": "+91…",
    "email": "guest@example.com",
    "message": "Interested in weekend stay",
    "status": "New",
    "created_at": "2026-05-07T..."
  }
}`}
          </pre>
        </div>

        {data.last_sync_at && (
          <div className="flex items-center gap-2 text-sm">
            {lastOk ? (
              <CheckCircle2 className="w-4 h-4 text-sage-700" strokeWidth={1.5} />
            ) : (
              <AlertCircle className="w-4 h-4 text-red-600" strokeWidth={1.5} />
            )}
            <span className="text-earth-700">
              Last sync: <span className="font-medium">{data.last_sync_status}</span> ·{" "}
              <span className="text-earth-500">{new Date(data.last_sync_at).toLocaleString()}</span>
            </span>
          </div>
        )}

        <div className="flex flex-wrap gap-3">
          <Button
            onClick={save}
            disabled={saving}
            className="bg-earth-900 text-white hover:bg-earth-800 rounded-full px-7"
            data-testid="crm-save-btn"
          >
            {saving ? "Saving…" : "Save changes"}
          </Button>
          <Button
            onClick={test}
            disabled={testing || !data.webhook_url}
            variant="outline"
            className="rounded-full px-7 border-[#FF6B35]/40 text-[#FF6B35] hover:bg-[#FF6B35]/10"
            data-testid="crm-test-btn"
          >
            {testing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" strokeWidth={1.5} /> : <Send className="w-4 h-4 mr-2" strokeWidth={1.5} />}
            Send test enquiry
          </Button>
        </div>

        {testResult && (
          <div className={`text-sm rounded-xl px-4 py-3 ${testResult.ok ? "bg-sage-50 text-sage-800 border border-sage-200" : "bg-red-50 text-red-800 border border-red-200"}`}>
            {testResult.ok ? "✓ " : "✕ "} {testResult.status}
          </div>
        )}
      </div>
    </div>
  );
}
